import * as React from "react";
import RevisionAccordion from "./revisionAccordionComponent";
import { heldStockSummary, newBatchId } from "../common/inventory";

export default function SimpleAccordion(props) {
  const data = props.data;
  const parentQuotation = props.parentQuotation;
  // Changes when a status change has finished moving stock. A Parse object being
  // mutated does not re-render React by itself, so this is the signal to recompute.
  const stockSettledAt = props.stockSettledAt;
  // True while units are actually in motion. Finalising during that window would read
  // a half finished picture, so the button is held closed until it lands.
  //
  // Stock moves from two places: the parent's status dropdown, which reports through
  // these props, and a revision's own dropdown, which reports through the callbacks
  // handed to each accordion below. Either one has to settle the same picture.
  const [localStockMoving, setLocalStockMoving] = React.useState(false);
  const [localSettledAt, setLocalSettledAt] = React.useState(0);
  const stockMoving = props.stockMoving || localStockMoving;

  // Which orders in this family still have units out of stock. A revision cannot be
  // finalised while one of them does: the parent's status dropdown disappears the
  // moment a revision is finalised, so its units would have no way back and the same
  // order would end up deducted twice.
  //
  // Read straight off the objects already on the page rather than queried. The parent
  // and the revisions are the whole family, restoreOrderedStock clears the flag on the
  // very object held in state, so the answer is available the instant it changes. A
  // query here was a second round trip on top of the stock saves, which is what made
  // the warning linger after the parent had already been reopened.
  const stockHolders = React.useMemo(() => {
    const family = [parentQuotation, ...(data ?? [])].filter(Boolean);
    return family
      .filter((member) => member.get('stockDeductedAt'))
      .map((member) => ({
        id: member.id,
        quotationNumber: member.get('quotationNumber'),
        status: member.get('status') || 'no status',
        ...heldStockSummary(member),
      }));
    // stockSettledAt is a deliberate dependency: it is the only thing that changes when
    // the flag on an unchanged object reference is cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentQuotation, data, stockSettledAt, localSettledAt]);

  const finalizeRevision = async (id) => {
    // The button is disabled in both these cases; this is the backstop for a page whose
    // data went stale while it sat open.
    if (stockMoving || stockHolders.some((holder) => holder.id !== id)) {
      return;
    }

    const batchId = newBatchId();

    // Sequentially, and with the reload afterwards. The previous version reloaded
    // inside the loop, so on an order with more than one revision the later saves were
    // cut off by the page going away.
    for (const revision of data) {
      revision.set('finalized', revision.id === id);
      await revision.save(null, { context: { batchId } });
    }

    window.location.reload();
  };

  return (
    <div>
      {data.map((revision, index) => (
        <RevisionAccordion
          key={index}
          revision={revision}
          finalizeRevision={finalizeRevision}
          onCreateRevision={props.onCreateRevision}
          stockHolders={stockHolders.filter((holder) => holder.id !== revision.id)}
          stockMoving={stockMoving}
          onStockMoving={setLocalStockMoving}
          onStockSettled={() => setLocalSettledAt(Date.now())}
        />
      ))}
    </div>
  );
}
